'use client'

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { AI_PERMISSIONS } from '@/lib/auth/permissions.constants'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Loader2,
  Plus,
  Pencil,
  Key,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Cpu,
  Play,
} from 'lucide-react'
import { toast } from 'sonner'
import { ProviderDialog } from './provider-dialog'
import { ModelDialog } from './model-dialog'
import { ModelTestDialog } from './model-test-dialog'
import type { AIProvider, ProviderFormState, AIModel, ModelFormState } from './types'
import { microusdToUsd, usdToMicrousd } from './types'
import { formatModelCost } from '@/lib/ai/model-cost'

const DEFAULT_MODEL_FORM: ModelFormState = {
  model_name: '',
  display_name: '',
  model_type: 'chat',
  supports_streaming: true,
  supports_function_calling: false,
  supports_vision: false,
  cost_input_per_1m_usd: '',
  cost_output_per_1m_usd: '',
  is_enabled: true,
  config_json: '{}',
}

export function AIProvidersPage() {
  const { user, isLoading: authLoading, fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const [providers, setProviders] = useState<AIProvider[]>([])
  const [models, setModels] = useState<AIModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set())

  // Provider dialog state
  const [isProviderDialogOpen, setIsProviderDialogOpen] = useState(false)
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null)
  const [isSavingProvider, setIsSavingProvider] = useState(false)
  const [providerFormData, setProviderFormData] = useState<ProviderFormState>({
    name: '',
    display_name: '',
    api_key: '',
    base_url: '',
    is_enabled: true,
  })

  // Model dialog state
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<AIModel | null>(null)
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null)
  const [isSavingModel, setIsSavingModel] = useState(false)
  const [modelFormData, setModelFormData] = useState<ModelFormState>(DEFAULT_MODEL_FORM)

  // Model test dialog state (for testing from list)
  const [testingModel, setTestingModel] = useState<AIModel | null>(null)
  const [testingProviderName, setTestingProviderName] = useState('')

  const hasViewPermission = can(AI_PERMISSIONS.VIEW)
  const hasManageProvidersPermission = can(AI_PERMISSIONS.MANAGE_PROVIDERS)
  const hasManageModelsPermission = can(AI_PERMISSIONS.MANAGE_MODELS)

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)
      const [providersRes, modelsRes] = await Promise.all([
        fetchWithAuth('/api/ai/providers'),
        fetchWithAuth('/api/ai/models'),
      ])

      if (!providersRes.ok) throw new Error('Failed to fetch providers')
      if (!modelsRes.ok) throw new Error('Failed to fetch models')

      const providersData = await providersRes.json()
      const modelsData = await modelsRes.json()

      setProviders(providersData.providers || [])
      setModels(modelsData.models || [])
    } catch (error) {
      console.error('Failed to fetch data:', error)
      toast.error('Failed to load AI providers')
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => {
    if (!authLoading && hasViewPermission) {
      fetchData()
    }
  }, [authLoading, hasViewPermission, fetchData])

  const getModelsForProvider = (providerId: string) => {
    return models.filter((m) => m.provider_id === providerId)
  }

  const toggleProviderExpanded = (providerId: string) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev)
      if (next.has(providerId)) {
        next.delete(providerId)
      } else {
        next.add(providerId)
      }
      return next
    })
  }

  // Provider handlers
  const handleOpenProviderDialog = (provider?: AIProvider) => {
    if (provider) {
      setEditingProvider(provider)
      setProviderFormData({
        name: provider.name,
        display_name: provider.display_name,
        api_key: '',
        base_url: provider.base_url || '',
        is_enabled: provider.is_enabled,
      })
    } else {
      setEditingProvider(null)
      setProviderFormData({
        name: '',
        display_name: '',
        api_key: '',
        base_url: '',
        is_enabled: true,
      })
    }
    setIsProviderDialogOpen(true)
  }

  const handleSaveProvider = async () => {
    try {
      setIsSavingProvider(true)

      if (editingProvider) {
        const res = await fetchWithAuth(`/api/ai/providers/${editingProvider.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            display_name: providerFormData.display_name,
            api_key: providerFormData.api_key || undefined,
            base_url: providerFormData.base_url || undefined,
            is_enabled: providerFormData.is_enabled,
          }),
        })

        if (!res.ok) {
          const error = await res.json()
          throw new Error(error.error || 'Failed to update provider')
        }

        toast.success('Provider updated successfully')
      } else {
        const res = await fetchWithAuth('/api/ai/providers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(providerFormData),
        })

        if (!res.ok) {
          const error = await res.json()
          throw new Error(error.error || 'Failed to create provider')
        }

        toast.success('Provider created successfully')
      }

      setIsProviderDialogOpen(false)
      fetchData()
    } catch (error) {
      console.error('Failed to save provider:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save provider')
    } finally {
      setIsSavingProvider(false)
    }
  }

  const handleToggleProviderEnabled = async (provider: AIProvider) => {
    try {
      const res = await fetchWithAuth(`/api/ai/providers/${provider.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_enabled: !provider.is_enabled,
        }),
      })

      if (!res.ok) throw new Error('Failed to update provider')

      toast.success(`Provider ${!provider.is_enabled ? 'enabled' : 'disabled'}`)
      fetchData()
    } catch (error) {
      console.error('Failed to toggle provider:', error)
      toast.error('Failed to update provider')
    }
  }

  // Model handlers
  const handleOpenModelDialog = (providerId: string, model?: AIModel) => {
    setSelectedProviderId(providerId)
    if (model) {
      setEditingModel(model)
      setModelFormData({
        model_name: model.model_name,
        display_name: model.display_name,
        model_type: model.model_type,
        supports_streaming: model.supports_streaming,
        supports_function_calling: model.supports_function_calling,
        supports_vision: model.supports_vision,
        cost_input_per_1m_usd: microusdToUsd(model.cost_input_per_1m_microusd),
        cost_output_per_1m_usd: microusdToUsd(model.cost_output_per_1m_microusd),
        is_enabled: model.is_enabled,
        config_json: JSON.stringify(model.config || {}, null, 2),
      })
    } else {
      setEditingModel(null)
      setModelFormData(DEFAULT_MODEL_FORM)
    }
    setIsModelDialogOpen(true)
  }

  const handleSaveModel = async ({ keepOpen = false }: { keepOpen?: boolean } = {}): Promise<boolean> => {
    if (!selectedProviderId) return false

    try {
      setIsSavingModel(true)

      // Parse config JSON
      let config: Record<string, unknown> = {}
      try {
        config = JSON.parse(modelFormData.config_json || '{}')
      } catch {
        toast.error('Invalid JSON in Default Configuration')
        return false
      }

      const payload = {
        provider_id: selectedProviderId,
        model_name: modelFormData.model_name,
        display_name: modelFormData.display_name,
        model_type: modelFormData.model_type,
        supports_streaming: modelFormData.supports_streaming,
        supports_function_calling: modelFormData.supports_function_calling,
        supports_vision: modelFormData.supports_vision,
        cost_input_per_1m_microusd: usdToMicrousd(modelFormData.cost_input_per_1m_usd),
        cost_output_per_1m_microusd: usdToMicrousd(modelFormData.cost_output_per_1m_usd),
        is_enabled: modelFormData.is_enabled,
        config,
      }

      let savedModel: AIModel
      if (editingModel) {
        const res = await fetchWithAuth(`/api/ai/models/${editingModel.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (!res.ok) {
          const error = await res.json()
          throw new Error(error.error || 'Failed to update model')
        }

        savedModel = (await res.json()).model
        toast.success('Model updated successfully')
      } else {
        const res = await fetchWithAuth('/api/ai/models', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (!res.ok) {
          const error = await res.json()
          throw new Error(error.error || 'Failed to create model')
        }

        savedModel = (await res.json()).model
        toast.success('Model created successfully')
      }

      // Reflect the persisted record so a subsequent save updates (not re-creates)
      // it, and any test runs against the just-saved config.
      setEditingModel(savedModel)
      await fetchData()
      if (!keepOpen) setIsModelDialogOpen(false)
      return true
    } catch (error) {
      console.error('Failed to save model:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save model')
      return false
    } finally {
      setIsSavingModel(false)
    }
  }

  const handleDeleteModel = async () => {
    if (!editingModel) return

    if (!confirm(`Are you sure you want to delete "${editingModel.display_name}"?`)) {
      return
    }

    try {
      setIsSavingModel(true)

      const res = await fetchWithAuth(`/api/ai/models/${editingModel.id}`, {
        method: 'DELETE',
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to delete model')
      }

      toast.success('Model deleted successfully')
      setIsModelDialogOpen(false)
      fetchData()
    } catch (error) {
      console.error('Failed to delete model:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete model')
    } finally {
      setIsSavingModel(false)
    }
  }

  const handleToggleModelEnabled = async (model: AIModel) => {
    try {
      const res = await fetchWithAuth(`/api/ai/models/${model.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_enabled: !model.is_enabled,
        }),
      })

      if (!res.ok) throw new Error('Failed to update model')

      toast.success(`Model ${!model.is_enabled ? 'enabled' : 'disabled'}`)
      fetchData()
    } catch (error) {
      console.error('Failed to toggle model:', error)
      toast.error('Failed to update model')
    }
  }

  const getSelectedProviderName = () => {
    const provider = providers.find((p) => p.id === selectedProviderId)
    return provider?.display_name || 'Provider'
  }

  // Canonical provider key ('openai' | 'anthropic' | 'google') — drives
  // provider-specific logic (doc links, config placeholder), unlike the
  // human-facing display name.
  const getSelectedProviderKey = () => {
    const provider = providers.find((p) => p.id === selectedProviderId)
    return provider?.name || ''
  }

  if (authLoading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-64 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!user || !hasViewPermission) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to view AI providers. Contact your administrator for access.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">AI Providers</h1>
            <p className="text-gray-600 mt-1">
              Manage AI providers, API keys, and models
            </p>
          </div>
          {hasManageProvidersPermission && (
            <Button onClick={() => handleOpenProviderDialog()} className="gap-2">
              <Plus className="h-4 w-4" />
              <span>Add Provider</span>
            </Button>
          )}
        </div>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>How it works</AlertTitle>
          <AlertDescription>
            Configure API keys for AI providers, then add or manage models for each provider.
            Models can be assigned to use cases in the "Use Cases" tab.
          </AlertDescription>
        </Alert>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        )}

        {!isLoading && providers.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-gray-500">
                <p className="text-lg font-medium mb-2">No AI providers configured</p>
                <p className="text-sm mb-4">
                  Add your first AI provider to get started
                </p>
                {hasManageProvidersPermission && (
                  <Button onClick={() => handleOpenProviderDialog()} className="gap-2">
                    <Plus className="h-4 w-4" />
                    <span>Add Provider</span>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoading &&
          providers.map((provider) => {
            const providerModels = getModelsForProvider(provider.id)
            const isExpanded = expandedProviders.has(provider.id)

            return (
              <Card key={provider.id} className="relative">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        {provider.display_name}
                        {provider.is_enabled ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-400" />
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        <span>Provider:</span> {provider.name}
                      </CardDescription>
                    </div>
                    {hasManageProvidersPermission && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenProviderDialog(provider)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-gray-600" />
                      <span className="text-sm text-gray-700">API Key</span>
                    </div>
                    <span
                      className={`text-sm font-medium ${
                        provider.has_api_key ? 'text-green-600' : 'text-gray-500'
                      }`}
                    >
                      {provider.has_api_key ? 'Configured' : 'Not configured'}
                    </span>
                  </div>

                  {provider.base_url && (
                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">Base URL</span>
                      <span className="text-sm text-gray-600 font-mono">
                        {provider.base_url}
                      </span>
                    </div>
                  )}

                  {/* Models Section */}
                  <Collapsible
                    open={isExpanded}
                    onOpenChange={() => toggleProviderExpanded(provider.id)}
                  >
                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <Cpu className="h-4 w-4" />
                        <span>Models</span>
                        <Badge variant="secondary" className="ml-2">
                          {providerModels.length}
                        </Badge>
                      </CollapsibleTrigger>
                      {hasManageModelsPermission && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenModelDialog(provider.id)
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <CollapsibleContent className="mt-2 space-y-2">
                      {providerModels.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                          No models configured for this provider
                        </div>
                      ) : (
                        providerModels.map((model) => (
                          <div
                            key={model.id}
                            className="flex items-center justify-between py-2 px-3 bg-white border rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={model.is_enabled}
                                onCheckedChange={() => handleToggleModelEnabled(model)}
                                disabled={!hasManageModelsPermission}
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {model.display_name}
                                </div>
                                <div className="text-xs text-gray-500 font-mono">
                                  {model.model_name}
                                </div>
                                {formatModelCost(
                                  model.cost_input_per_1m_microusd,
                                  model.cost_output_per_1m_microusd,
                                ) && (
                                  <div className="text-xs text-gray-500">
                                    {formatModelCost(
                                      model.cost_input_per_1m_microusd,
                                      model.cost_output_per_1m_microusd,
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={model.model_type === 'chat' ? 'default' : 'outline'}
                                className="text-xs"
                              >
                                {model.model_type}
                              </Badge>
                              {model.supports_function_calling && (
                                <Badge variant="outline" className="text-xs">
                                  Tools
                                </Badge>
                              )}
                              {model.supports_vision && (
                                <Badge variant="outline" className="text-xs">
                                  Vision
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setTestingModel(model)
                                  setTestingProviderName(provider.display_name)
                                }}
                                title="Test model"
                              >
                                <Play className="h-3 w-3" />
                              </Button>
                              {hasManageModelsPermission && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenModelDialog(provider.id, model)}
                                  title="Edit model"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </CollapsibleContent>
                  </Collapsible>

                  {hasManageProvidersPermission && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <Label
                        htmlFor={`enabled-${provider.id}`}
                        className="text-sm text-gray-700"
                      >
                        Enable provider
                      </Label>
                      <Switch
                        id={`enabled-${provider.id}`}
                        checked={provider.is_enabled}
                        onCheckedChange={() => handleToggleProviderEnabled(provider)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
      </div>

      <ProviderDialog
        open={isProviderDialogOpen}
        onOpenChange={setIsProviderDialogOpen}
        formData={providerFormData}
        onChange={setProviderFormData}
        onSave={handleSaveProvider}
        isSaving={isSavingProvider}
        editingProvider={editingProvider}
      />

      <ModelDialog
        open={isModelDialogOpen}
        onOpenChange={setIsModelDialogOpen}
        formData={modelFormData}
        onChange={setModelFormData}
        onSave={handleSaveModel}
        onDelete={editingModel ? handleDeleteModel : undefined}
        isSaving={isSavingModel}
        editingModel={editingModel}
        providerName={getSelectedProviderName()}
        providerKey={getSelectedProviderKey()}
        providerId={selectedProviderId || ''}
      />

      {/* Test dialog for testing from model list */}
      <ModelTestDialog
        open={!!testingModel}
        onOpenChange={(open) => {
          if (!open) {
            setTestingModel(null)
            setTestingProviderName('')
          }
        }}
        model={testingModel || undefined}
        providerName={testingProviderName}
      />
    </div>
  )
}
